/**
 * FiasAIInput - Chat input for the Fias AI panel.
 *
 * Structural readiness component. Will be used within FiasAIPanel for arche integration.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

interface FiasAIInputProps {
  onSubmit: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function FiasAIInput({
  onSubmit,
  disabled = false,
  placeholder = 'Ask Fias AI...',
}: FiasAIInputProps) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSubmit(trimmed);
    setValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="border-t border-border p-2">
      <div className="relative">
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="min-h-[36px] max-h-[120px] pr-10 resize-none rounded-lg text-sm bg-muted/50"
          rows={1}
          style={{ height: 'auto' }}
        />
        <Button
          type="button"
          size="icon"
          disabled={!value.trim() || disabled}
          onClick={handleSubmit}
          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 rounded-md"
        >
          <Send className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
